import { TextField } from 'mui-rff';
import React from 'react';
import { useField } from 'react-final-form';

import RadioTabsWrapper from '@dashboard/components/editor/EditorSidebar/inputs/RadioTabsWrapper';
import { DeleteOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { type TBlock } from '@wp-block-builder/package';

import ImageManager from './ImageManager';

type TImageBlock = Extract<TBlock, { type: 'Image' }>;

type Props = {
  // Since this is a "special input", the name property points to the root block
  name: string;
};

function useFieldInput<TResult>(name: string) {
  return useField<TResult>(name).input;
}

function isBlank(value: unknown) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return true;
  }

  return false;
}

const OPTIONS = [
  {
    value: 'UPLOAD',
    label: 'Upload',
  },
  {
    value: 'URL_TEMPLATE',
    label: 'URL',
  },
];

export default function ImageUploadInput({ name }: Props) {
  const urlInput = useFieldInput<NonNullable<TImageBlock['props']>['url']>(`${name}.props.url`);
  const uploadIdInput = useFieldInput<NonNullable<TImageBlock['props']>['uploadId']>(`${name}.props.uploadId`);
  const imageTypeInput = useFieldInput<NonNullable<TImageBlock['props']>['imageType']>(`${name}.props.imageType`);

  const imageType = imageTypeInput.value || 'UPLOAD';
  const handleImageTypeChange = (v: unknown) => {
    const imageType = v === 'URL_TEMPLATE' ? 'URL_TEMPLATE' : 'UPLOAD';
    imageTypeInput.onChange(imageType);
  };

  const onRemoveImage = () => {
    urlInput.onChange(null);
    uploadIdInput.onChange(null);
  };

  const renderPanel = () => {
    switch (imageType) {
      case 'UPLOAD':
        return (
          <Stack spacing={0.5}>
            <ImageManager name={name} />

            <Box textAlign="center">
              <IconButton size="small" disabled={isBlank(urlInput.value)} onClick={onRemoveImage}>
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        );
      case 'URL_TEMPLATE':
        return (
          <Box sx={{ p: 0.5, mb: 1 }}>
            <TextField
              fullWidth
              name={`${name}.props.urlTemplate`}
              variant="standard"
              placeholder="{{product.thumbnailUrl}}"
              label="Hosted image URL"
              size="small"
              margin="normal"
            />
          </Box>
        );
    }
  };

  return (
    <RadioTabsWrapper label="Image source" options={OPTIONS} onChange={handleImageTypeChange} value={imageType}>
      {renderPanel()}
    </RadioTabsWrapper>
  );
}
